import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

/**
 * Componente de contenido de TicketView que puede ser usado tanto en la página real como en el preview
 * @param {Object} props
 * @param {Object} props.ticket - Datos del ticket
 * @param {Object} props.texts - Textos personalizados
 * @param {Object} props.styles - Estilos personalizados
 * @param {string} props.layout - Layout seleccionado ('wallet', 'dark', 'minimal')
 * @param {Object} props.layoutOptions - Opciones de layout
 * @param {Object} [props.heroImage] - Imagen hero de la personalización (con url, cdnUrl, etc.)
 * @param {Function} [props.onShare] - Callback cuando se comparte (opcional, para preview)
 * @param {boolean} props.isPreview - Si es true, desactiva funcionalidad interactiva
 */
const TicketViewContent = ({
    ticket,
    texts = {},
    styles = {},
    layout = 'wallet',
    layoutOptions = {},
    heroImage = null,
    onShare,
    isPreview = false
}) => {
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

    // Calcular estilos aplicando personalización
    const cardBgColor = styles.cardBackgroundColor || '#FFFFFF';
    const headerBgColor = styles.headerBackgroundColor || '#3B82F6';
    const headerTextColor = styles.headerTextColor || '#FFFFFF';
    const textColor = styles.textColor || '#111827';
    const secondaryTextColor = styles.secondaryTextColor || '#6B7280';
    const pointsColor = styles.pointsColor || '#3B82F6';
    const pointsLabelColor = styles.pointsLabelColor || '#6B7280';
    const qrBgColor = styles.qrBackgroundColor || '#FFFFFF';
    const qrBorderColor = styles.qrBorderColor || '#E5E7EB';
    const dividerColor = styles.dividerColor || '#E5E7EB';
    const iconColor = styles.iconColor || '#3B82F6';
    const fontFamily = styles.fontFamily || 'sans-serif';
    const fontSize = styles.fontSize || '16px';
    const cardBorderRadius = styles.cardBorderRadius || '24px';

    // Textos personalizados con valores por defecto
    const cardTitle = texts.cardTitle || 'Tarjeta de Fidelización';
    const pointsLabel = texts.pointsLabel || 'puntos';
    const missingPointsText = texts.missingPointsText || 'Te faltan';
    const pointsAvailableText = texts.pointsAvailableText || 'puntos disponibles';
    const pointsToRedeemText = texts.pointsToRedeemText || '¡Tienes {points} puntos para canjear!';
    const accumulatePointsText = texts.accumulatePointsText || 'Acumula puntos para canjear premios';
    const scanToAccumulateText = texts.scanToAccumulateText || 'Escanea para acumular puntos';
    const presentCodeText = texts.presentCodeText || 'Presenta este código en la tienda para acumular puntos automáticamente';
    const memberLabel = texts.memberLabel || 'Miembro';
    const storeLabel = texts.storeLabel || 'Tienda';
    const userNameLabel = texts.userNameLabel || 'usuario';
    const viewOnMapsText = texts.viewOnMapsText || 'Ver en Google Maps';
    const shareCardText = texts.shareCardText || 'Compartir Tarjeta';
    const ticketGeneratedText = texts.ticketGeneratedText || 'Ticket generado el';
    const rewardsAvailableText = texts.rewardsAvailableText || 'Premios disponibles para canjear';

    // Obtener datos del ticket
    const isPointsType = ticket?.tipoFidelizacion === 'PUNTOS';
    
    // Para tipo ACUMULACION: usar ticket.puntos directamente
    // La marca decide cuántos puntos acumula el cliente, no se calculan desde historial
    // ticket.puntos es la fuente de verdad (puntos disponibles para canjear)
    // Para tipo PUNTOS: también usar ticket.puntos (puntos acumulados para sellos)
    const puntosRaw = ticket?.puntos;
    const accumulatedPoints = Math.floor(puntosRaw || 0);
    const totalStampsNeeded = ticket?.marca?.puntosTicket || 0; // Número total de sellos necesarios
    
    // Para tarjetas tipo PUNTOS: cada punto acumulado = 1 sello completado
    // Ejemplo: 3 puntos acumulados = 3 sellos completados de 7 necesarios
    // puntosTicket es el número total de sellos necesarios, no puntos por sello
    const completedStamps = isPointsType 
        ? Math.min(accumulatedPoints, totalStampsNeeded) // No más sellos que los necesarios
        : 0;
    const missingPoints = isPointsType && totalStampsNeeded > 0
        ? Math.max(0, totalStampsNeeded - accumulatedPoints) // Puntos faltantes para completar todos los sellos
        : totalStampsNeeded - accumulatedPoints;
    
    const clienteNombre = ticket?.cliente ? `${ticket.cliente.nombre || ''} ${ticket.cliente.apellido || ''}`.trim() : 'Usuario';
    const marcaNombre = ticket?.marca?.nombre || 'Tu Marca';
    const marcaEslogan = ticket?.marca?.eslogan || '';
    const marcaLogo = ticket?.marca?.logotipo?.cdnUrl || ticket?.marca?.logo?.url || null;
    const tiendaNombre = ticket?.tienda?.nombre || '';
    const tiendaDireccion = ticket?.tienda ? `${ticket.tienda.via || ''} ${ticket.tienda.calle || ''} ${ticket.tienda.numero || ''}, ${ticket.tienda.poblacion || ''}`.trim() : '';
    const ticketCreatedAt = ticket?.created_at ? new Date(ticket.created_at).toLocaleDateString() : '';

    // Generar QR cuando el ticket esté disponible
    useEffect(() => {
        const generateQR = async () => {
            if (!ticket?._id || isPreview) {
                // En preview, generar un QR de ejemplo
                try {
                    const qrDataUrl = await QRCode.toDataURL('https://example.com/ticket/preview', {
                        width: 200,
                        margin: 2,
                        color: {
                            dark: '#000000',
                            light: '#FFFFFF'
                        }
                    });
                    setQrCodeDataUrl(qrDataUrl);
                } catch (error) {
                    console.error('Error generando QR de preview:', error);
                }
                return;
            }

            try {
                const baseUrl = window.location.origin;
                const qrEndpointUrl = `${baseUrl}/api/tickets/${ticket._id}/qr-accumulate`;

                const qrDataUrl = await QRCode.toDataURL(qrEndpointUrl, {
                    width: 200,
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    }
                });
                setQrCodeDataUrl(qrDataUrl);
            } catch (error) {
                console.error('Error generando QR del ticket:', error);
            }
        };

        generateQR();
    }, [ticket, isPreview]);

    // Función para compartir
    const handleShare = () => {
        if (isPreview) {
            return;
        }

        if (onShare) {
            onShare();
            return;
        }

        const shareData = {
            title: texts.shareTitle || 'Mi Tarjeta de Fidelización',
            text: texts.shareText || `Tarjeta de ${marcaNombre}`,
            url: window.location.href
        };

        if (navigator.share) {
            navigator.share(shareData);
        } else {
            navigator.clipboard.writeText(window.location.href);
            // En producción, usar Swal aquí
        }
    };

    // Función helper para renderizar sellos (círculos con checkmarks)
    const renderStamps = (stampsNeeded, completedStamps, containerClass = '') => {
        return (
            <div className={`grid grid-cols-5 gap-3 ${containerClass}`}>
                {Array.from({ length: stampsNeeded }, (_, i) => {
                    const isCompleted = i < completedStamps;
                    return (
                        <div
                            key={i}
                            className={`relative w-full aspect-square rounded-full border-4 flex items-center justify-center transition-all ${isCompleted
                                    ? 'bg-green-500 border-green-600 shadow-md scale-105'
                                    : 'bg-white border-gray-300'
                                }`}
                            style={{
                                borderColor: isCompleted ? '#10B981' : dividerColor,
                                backgroundColor: isCompleted ? '#10B981' : 'transparent'
                            }}
                        >
                            {isCompleted && (
                                <svg
                                    className="w-6 h-6 sm:w-8 sm:h-8 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Renderizar según el layout
    const renderContent = () => {
        if (layout === 'dark') {
            // Layout Dark: Estilo negro como Empanada Club
            return (
                <div className="bg-black rounded-[24px] shadow-xl overflow-hidden relative" style={{ borderRadius: cardBorderRadius }}>
                    {/* Header negro con usuario */}
                    <div className="bg-black text-white p-6" style={{ backgroundColor: headerBgColor, color: headerTextColor }}>
                        <div className="flex items-center justify-end">
                            {layoutOptions.showUserInfo !== false && (
                                <div className="text-right">
                                    <p className="text-sm opacity-80">{userNameLabel}</p>
                                    <p className="text-base font-semibold">{clienteNombre}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hero Image */}
                    {heroImage && (
                        <div className="w-full">
                            <img
                                src={heroImage.cdnUrl || heroImage.url}
                                alt={`${marcaNombre} Hero`}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                    )}

                    {/* Sección de slogan, nombre de marca y puntos */}
                    <div className="bg-black p-6" style={{ backgroundColor: cardBgColor === '#FFFFFF' ? '#000000' : cardBgColor }}>
                        <div className="flex items-start justify-between mb-6">
                            {/* Izquierda: Slogan y nombre de marca */}
                            <div className="flex-1">
                                {layoutOptions.showSlogan !== false && marcaEslogan && (
                                    <p className="text-sm mb-2" style={{ color: secondaryTextColor }}>{marcaEslogan}</p>
                                )}
                                {layoutOptions.showBrandName !== false && (
                                    <h2 className="text-2xl font-bold" style={{ color: textColor }}>{marcaNombre}</h2>
                                )}
                            </div>

                            {/* Derecha: Label de puntos y puntos */}
                            <div className="text-right">
                                <p className="text-sm mb-1" style={{ color: pointsLabelColor }}>
                                    {texts.pointsLabelCustom || pointsLabel}
                                </p>
                                <p className="text-2xl font-bold" style={{ color: pointsColor }}>{accumulatedPoints}</p>
                            </div>
                        </div>

                        {/* Sellos para tipo PUNTOS */}
                        {isPointsType && layoutOptions.showPointsSection !== false && (
                            <div className="mb-6">
                                <div className="text-center mb-3">
                                    <p className="text-sm mb-2" style={{ color: secondaryTextColor }}>
                                        {completedStamps}/{totalStampsNeeded} sellos completados ({accumulatedPoints} {pointsLabel})
                                    </p>
                                </div>
                                {renderStamps(totalStampsNeeded, completedStamps)}
                            </div>
                        )}

                        {/* QR Code centrado */}
                        {layoutOptions.showQRCode !== false && (
                            <div className="flex justify-center">
                                <div className="p-4 rounded-xl" style={{ backgroundColor: qrBgColor, border: `2px solid ${qrBorderColor}` }}>
                                    {qrCodeDataUrl ? (
                                        <img
                                            src={qrCodeDataUrl}
                                            alt="QR Code para acumulación de puntos"
                                            className="w-40 h-40 object-contain"
                                        />
                                    ) : (
                                        <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <svg className="w-20 h-20 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-3 0h2v2h-2v-2zm3 3h3v3h-3v-3zm-3 0h2v3h-2v-3z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        } else if (layout === 'minimal') {
            // Layout Minimal/Moderno: Diseño moderno y elegante con gradientes
            return (
                <div className="bg-white rounded-[24px] shadow-xl overflow-hidden relative" style={{ backgroundColor: cardBgColor, borderRadius: cardBorderRadius }}>
                    {/* Header con gradiente moderno */}
                    <div
                        className="p-6 text-white relative overflow-hidden"
                        style={{
                            background: `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBgColor}dd 100%)`,
                            color: headerTextColor
                        }}
                    >
                        {/* Patrón decorativo sutil */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                {marcaLogo && layoutOptions.showLogo !== false && (
                                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl p-2 flex items-center justify-center backdrop-blur-sm">
                                        <img
                                            src={marcaLogo}
                                            alt={marcaNombre}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                )}
                                <div className="flex-1 ml-4">
                                    {layoutOptions.showBrandName !== false && (
                                        <h1 className="text-2xl font-bold mb-1">{marcaNombre}</h1>
                                    )}
                                    {layoutOptions.showSlogan !== false && marcaEslogan && (
                                        <p className="text-sm opacity-90">{marcaEslogan}</p>
                                    )}
                                </div>
                            </div>

                            {/* Contador de puntos destacado */}
                            <div className="bg-white bg-opacity-20 rounded-xl p-4 backdrop-blur-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs opacity-80 mb-1">{texts.pointsLabelCustom || pointsLabel}</p>
                                        <p className="text-3xl font-bold">{accumulatedPoints}</p>
                                    </div>
                                    {isPointsType && totalStampsNeeded > 0 && (
                                        <div className="text-right">
                                            <p className="text-xs opacity-80 mb-1">Objetivo</p>
                                            <p className="text-2xl font-bold">{totalStampsNeeded}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contenido principal */}
                    <div className="p-6" style={{ backgroundColor: cardBgColor }}>
                        {/* Sellos para tipo PUNTOS */}
                        {isPointsType && layoutOptions.showPointsSection !== false && (
                            <div className="mb-6">
                                <div className="text-center mb-4">
                                    <h3 className="text-lg font-semibold mb-2" style={{ color: textColor }}>
                                        {cardTitle}
                                    </h3>
                                    <p className="text-sm" style={{ color: secondaryTextColor }}>
                                        {completedStamps}/{totalStampsNeeded} sellos completados ({accumulatedPoints} {pointsLabel})
                                    </p>
                                </div>
                                {renderStamps(totalStampsNeeded, completedStamps, 'mb-4')}
                                {missingPoints > 0 && (
                                    <p className="text-center text-sm mt-3" style={{ color: secondaryTextColor }}>
                                        {missingPointsText} <span className="font-semibold" style={{ color: pointsColor }}>{missingPoints}</span> {pointsLabel}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Sección de puntos para acumulado */}
                        {!isPointsType && layoutOptions.showPointsSection !== false && (
                            <div className="mb-6 text-center">
                                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-4" style={{
                                    background: `linear-gradient(135deg, ${pointsColor}15 0%, ${pointsColor}08 100%)`
                                }}>
                                    <div className="text-4xl font-bold mb-2" style={{ color: pointsColor }}>
                                        {accumulatedPoints}
                                    </div>
                                    <p className="text-sm font-medium" style={{ color: pointsLabelColor }}>
                                        {pointsAvailableText}
                                    </p>
                                </div>
                                {layoutOptions.showProgressBar !== false && totalStampsNeeded > 0 && (
                                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2" style={{ backgroundColor: dividerColor }}>
                                        <div
                                            className="h-2 rounded-full transition-all duration-500"
                                            style={{
                                                width: `${Math.min((accumulatedPoints / Math.max(totalStampsNeeded, 1)) * 100, 100)}%`,
                                                backgroundColor: pointsColor
                                            }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* QR Code centrado con estilo moderno */}
                        {layoutOptions.showQRCode !== false && (
                            <div className="text-center">
                                <h3 className="text-sm font-medium mb-3" style={{ color: secondaryTextColor }}>
                                    {scanToAccumulateText}
                                </h3>
                                <div className="inline-block p-4 rounded-2xl shadow-lg" style={{
                                    backgroundColor: qrBgColor,
                                    border: `2px solid ${qrBorderColor}`,
                                    boxShadow: `0 4px 6px -1px ${qrBorderColor}40`
                                }}>
                                    {qrCodeDataUrl ? (
                                        <img
                                            src={qrCodeDataUrl}
                                            alt="QR Code para acumulación de puntos"
                                            className="w-36 h-36 object-contain"
                                        />
                                    ) : (
                                        <div className="w-36 h-36 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <svg className="w-18 h-18 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-3 0h2v2h-2v-2zm3 3h3v3h-3v-3zm-3 0h2v3h-2v-3z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs mt-3" style={{ color: secondaryTextColor }}>{presentCodeText}</p>
                            </div>
                        )}
                    </div>
                </div>
            );
        } else {
            // Layout Wallet (default): Estilo tipo wallet con patrón superior/inferior
            return (
                <div className="bg-white rounded-[24px] shadow-xl overflow-hidden relative" style={{ backgroundColor: cardBgColor, borderRadius: cardBorderRadius }}>
                    {/* Patrón superior tipo wallet */}
                    {layoutOptions.showWalletPattern !== false && (
                        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-200 via-white to-gray-200"></div>
                    )}

                    {/* Header con logo y nombre */}
                    <div className="p-6" style={{ backgroundColor: headerBgColor, color: headerTextColor }}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {marcaLogo && layoutOptions.showLogo !== false && (
                                    <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center">
                                        <img
                                            src={marcaLogo}
                                            alt={marcaNombre}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                )}
                                <div>
                                    {layoutOptions.showBrandName !== false && (
                                        <h1 className="text-xl font-bold">{marcaNombre}</h1>
                                    )}
                                    {layoutOptions.showSlogan !== false && marcaEslogan && (
                                        <p className="text-sm opacity-80">{marcaEslogan}</p>
                                    )}
                                </div>
                            </div>

                            {/* Código QR pequeño en header */}
                            {layoutOptions.showQRInHeader !== false && (
                                <div className="w-12 h-12 bg-white rounded-lg p-1 flex items-center justify-center">
                                    {qrCodeDataUrl ? (
                                        <img
                                            src={qrCodeDataUrl}
                                            alt="QR Code"
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-3 0h2v2h-2v-2zm3 3h3v3h-3v-3zm-3 0h2v3h-2v-3z" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contenido principal */}
                    <div className="p-6">
                        {/* Sección de puntos */}
                        {layoutOptions.showPointsSection !== false && (
                            <div className="text-center mb-6">
                                <h2 className="text-lg font-medium mb-3" style={{ color: textColor }}>
                                    {cardTitle}
                                </h2>

                                {isPointsType ? (
                                    // Modelo de puntos con sellos (círculos con checkmarks)
                                    <>
                                        <div className="text-3xl font-bold mb-4" style={{ color: pointsColor }}>
                                            {completedStamps}/{totalStampsNeeded} sellos
                                        </div>
                                        <div className="text-sm mb-2" style={{ color: secondaryTextColor }}>
                                            {accumulatedPoints} {pointsLabel} acumulados
                                        </div>
                                        {/* Sellos (círculos con checkmarks) */}
                                        {renderStamps(totalStampsNeeded, completedStamps, 'mb-4')}
                                        <div className="mt-3" style={{ color: secondaryTextColor }}>
                                            {missingPointsText} <span className="font-semibold" style={{ color: pointsColor }}>{missingPoints}</span> {pointsLabel}
                                        </div>
                                    </>
                                ) : (
                                    // Modelo de puntos para canjear premios
                                    <>
                                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 mb-4">
                                            <div className="flex items-center justify-center mb-2">
                                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                    </svg>
                                                </div>
                                                <div className="text-left">
                                                    <div className="text-2xl font-bold text-green-600">
                                                        {accumulatedPoints}
                                                    </div>
                                                    <div className="text-sm" style={{ color: secondaryTextColor }}>{pointsAvailableText}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Barra de progreso visual */}
                                        {layoutOptions.showProgressBar !== false && totalStampsNeeded > 0 && (
                                            <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                                                <div
                                                    className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${Math.min((accumulatedPoints / Math.max(totalStampsNeeded, 1)) * 100, 100)}%`
                                                    }}
                                                ></div>
                                            </div>
                                        )}

                                        <div className="text-sm" style={{ color: secondaryTextColor }}>
                                            {accumulatedPoints > 0 ? (
                                                <span className="text-green-600 font-medium">
                                                    {pointsToRedeemText.replace('{points}', accumulatedPoints)}
                                                </span>
                                            ) : (
                                                <span>{accumulatePointsText}</span>
                                            )}
                                        </div>

                                        {/* Indicador de próximos premios */}
                                        {ticket?.marca?.recompensas && ticket.marca.recompensas.length > 0 && layoutOptions.showRewardsIndicator !== false && (
                                            <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <div className="text-xs text-yellow-800 font-medium">
                                                    💎 {rewardsAvailableText}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* QR Code para TPV */}
                        {layoutOptions.showQRCode !== false && (
                            <div className="text-center mb-6">
                                <h3 className="text-sm font-medium mb-3" style={{ color: secondaryTextColor }}>
                                    {scanToAccumulateText}
                                </h3>
                                <div className="flex justify-center">
                                    <div className="p-4 rounded-xl shadow-lg border-2" style={{ backgroundColor: qrBgColor, borderColor: qrBorderColor }}>
                                        {qrCodeDataUrl ? (
                                            <img
                                                src={qrCodeDataUrl}
                                                alt="QR Code para acumulación de puntos"
                                                className="w-32 h-32 object-contain"
                                            />
                                        ) : (
                                            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                                <svg className="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-3 0h2v2h-2v-2zm3 3h3v3h-3v-3zm-3 0h2v3h-2v-3z" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-xs mt-2" style={{ color: secondaryTextColor }}>
                                    {presentCodeText}
                                </p>
                            </div>
                        )}

                        {/* Línea divisoria estilo wallet */}
                        {layoutOptions.showDivider !== false && (
                            <div className="relative py-4">
                                <div className="absolute left-0 right-0 h-[1px]" style={{ backgroundColor: dividerColor }}></div>
                                <div className="absolute -left-6 top-1/2 -mt-3 w-6 h-6 bg-gray-50 rounded-full"></div>
                                <div className="absolute -right-6 top-1/2 -mt-3 w-6 h-6 bg-gray-50 rounded-full"></div>
                            </div>
                        )}

                        {/* Información del cliente y tienda */}
                        {layoutOptions.showClientInfo !== false && (
                            <div className="space-y-4">
                                {/* Cliente */}
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-5 h-5" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <h3 className="font-medium" style={{ color: textColor }}>{memberLabel}</h3>
                                    </div>
                                    <div className="ml-7 space-y-1 text-sm" style={{ color: secondaryTextColor }}>
                                        <p>Nombre: {ticket?.cliente?.nombre} {ticket?.cliente?.apellido}</p>
                                        {layoutOptions.showEmail !== false && (
                                            <p>Email: {ticket?.cliente?.email}</p>
                                        )}
                                        {layoutOptions.showReferralCode !== false && ticket?.cliente?.codigoReferido && (
                                            <p>Código Referido: {ticket.cliente.codigoReferido}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Tienda */}
                                {layoutOptions.showStoreInfo !== false && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <svg className="w-5 h-5" style={{ color: iconColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <h3 className="font-medium" style={{ color: textColor }}>{storeLabel}</h3>
                                        </div>
                                        <div className="ml-7 space-y-1 text-sm" style={{ color: secondaryTextColor }}>
                                            <p>Nombre: {tiendaNombre}</p>
                                            <p>Dirección: {tiendaDireccion}</p>
                                            {layoutOptions.showMapsLink !== false && tiendaDireccion && (
                                                <a
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(tiendaDireccion)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center text-sm mt-1"
                                                    style={{ color: iconColor }}
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {viewOnMapsText}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {layoutOptions.showFooter !== false && (
                        <div className="border-t" style={{ borderColor: dividerColor }}>
                            <div className="p-4 text-center">
                                {layoutOptions.showCreatedDate !== false && ticketCreatedAt && (
                                    <p className="text-sm mb-4" style={{ color: secondaryTextColor }}>
                                        {ticketGeneratedText} {ticketCreatedAt}
                                    </p>
                                )}

                                {/* Botón de Compartir */}
                                {layoutOptions.showShareButton !== false && (
                                    <button
                                        onClick={handleShare}
                                        className="inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md transition-colors"
                                        style={{
                                            borderColor: dividerColor,
                                            color: textColor,
                                            backgroundColor: cardBgColor
                                        }}
                                        disabled={isPreview}
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                        </svg>
                                        {shareCardText}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Patrón inferior tipo wallet */}
                    {layoutOptions.showWalletPattern !== false && (
                        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-200 via-white to-gray-200"></div>
                    )}
                </div>
            );
        }
    };

    return (
        <div
            className="min-h-screen py-8 px-4 sm:px-6 lg:px-8"
            style={{
                fontFamily,
                fontSize,
                backgroundColor: styles.pageBackgroundColor || '#F9FAFB'
            }}
        >
            <div className="max-w-md mx-auto">
                {renderContent()}
            </div>
        </div>
    );
};

export default TicketViewContent;

